import { EnvironmentProviders, inject, provideAppInitializer } from '@angular/core';
import {
  buildClientContext,
  buildErrorPayload,
  isNoiseError,
  normalizeError,
  shouldReport,
  unwrapError,
} from './error-normalizer';
import { EventTrackerService } from './event-tracker.service';

/**
 * Registers the global `window` listeners (unhandled rejections, uncaught errors and resource
 * load failures). Port of legacy UNHANDLED_REJECTION_PROVIDER.
 */
export function provideGlobalErrorListeners(): EnvironmentProviders {
  return provideAppInitializer(() => {
    const tracker = inject(EventTrackerService);

    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      report(tracker, event.reason, 'UnhandledRejection');
    });

    // capture: true — resource 'error' events do not bubble.
    window.addEventListener(
      'error',
      (event: Event) => {
        const target = event.target;
        if (target && target !== window && (target as HTMLElement).tagName) {
          reportResourceError(tracker, target as HTMLElement);
          return;
        }
        const errorEvent = event as ErrorEvent;
        report(tracker, errorEvent.error ?? errorEvent.message, 'WindowError');
      },
      true,
    );
  });
}

function report(tracker: EventTrackerService, raw: unknown, fallbackType: string): void {
  const normalized = normalizeError(unwrapError(raw));
  if (isNoiseError(normalized) || !shouldReport(normalized)) return;

  tracker.track({
    event_type: 'js_error',
    event_name: 'js_error_captured',
    result: 'fail',
    error_kind: 'exception',
    payload: {
      ...buildErrorPayload(normalized),
      ...(normalized.type === 'Error' && { error_type: fallbackType }),
    },
  });
}

function reportResourceError(tracker: EventTrackerService, element: HTMLElement): void {
  const resourceUrl = (element as HTMLScriptElement).src || (element as HTMLLinkElement).href || '';
  if (!resourceUrl) return;

  if (/(?:chrome|moz|safari|ms-browser)-extension:\/\//i.test(resourceUrl)) return;

  const normalized = normalizeError({
    name: 'ResourceLoadError',
    message: `Failed to load ${element.tagName.toLowerCase()}: ${resourceUrl}`,
  });
  if (!shouldReport(normalized)) return;

  tracker.track({
    event_type: 'js_error',
    event_name: 'js_error_captured',
    result: 'fail',
    error_kind: 'network',
    payload: {
      error_message: normalized.message,
      error_type: 'ResourceLoadError',
      stack_hash: 'resource_load',
      resource_url: resourceUrl,
      resource_tag: element.tagName.toLowerCase(),
      ...buildClientContext(),
    },
  });
}
